import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "motion/react";
import { Heart, Target, Sparkles, Users, ArrowRight } from "lucide-react";

export const Route = createFileRoute("/nosotros")({
  head: () => ({
    meta: [
      { title: "Nosotros — PRICO" },
      { name: "description", content: "Conoce la historia, misión y valores de PRICO, la red social del mejor precio nacida en Colombia." },
      { property: "og:title", content: "Nosotros — PRICO" },
      { property: "og:description", content: "La historia detrás de PRICO." },
      { property: "og:url", content: "/nosotros" },
    ],
    links: [{ rel: "canonical", href: "/nosotros" }],
  }),
  component: AboutPage,
});

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

function AboutPage() {
  return (
    <>
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-warm)" }} />
        <div className="container-prico py-20 md:py-28 text-center max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <span className="chip">Quiénes somos</span>
            <h1 className="text-5xl md:text-7xl font-black mt-5 leading-[1]">
              Nacimos buscando un <span style={{ background: "var(--gradient-primary)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>precio justo</span>.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground">
              PRICO empezó en 2015 como una idea simple: que cualquier ciudadano pueda saber
              dónde están los mejores precios sin perderse entre facturas y recibos.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="container-prico py-20 grid lg:grid-cols-2 gap-14 items-center">
        <motion.div {...fadeUp} className="space-y-4">
          <span className="chip">Historia</span>
          <h2 className="text-4xl md:text-5xl font-black">De una hoja de cálculo a una comunidad.</h2>
          <p className="text-muted-foreground">
            Todo empezó cuando uno de nuestros fundadores notó que el Daflon costaba 30 mil
            pesos menos en Olímpica los días 15 y 30. ¿Y si pudiéramos compartir esa
            información con todo el mundo? Esa pregunta dio origen a PRICO.
          </p>
          <p className="text-muted-foreground">
            Hoy, una década después, somos un equipo apasionado por democratizar el acceso al
            mejor precio. Construimos tecnología con propósito: ayudar a familias colombianas
            y latinoamericanas a estirar cada peso.
          </p>
        </motion.div>
        <motion.div {...fadeUp} className="grid grid-cols-2 gap-4">
          {[
            { y: "2015", t: "Idea inicial" },
            { y: "2022", t: "Retomamos el sueño" },
            { y: "2024", t: "Logo y diseño" },
            { y: "2026", t: "PRICO oficial" },
          ].map((m, i) => (
            <motion.div key={m.y} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }}
              className="card-soft p-6">
              <p className="font-display text-3xl font-black text-primary">{m.y}</p>
              <p className="text-sm text-muted-foreground mt-1">{m.t}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="container-prico py-20">
        <motion.div {...fadeUp} className="text-center max-w-2xl mx-auto mb-14">
          <span className="chip">Nuestros valores</span>
          <h2 className="text-4xl md:text-5xl font-black mt-4">Lo que nos mueve</h2>
        </motion.div>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            { i: Heart, t: "Cercanía", d: "Construimos para gente real, con problemas reales." },
            { i: Target, t: "Transparencia", d: "Sin trucos. El precio es el precio." },
            { i: Sparkles, t: "Curiosidad", d: "Siempre buscando mejores formas de ahorrar." },
            { i: Users, t: "Comunidad", d: "Cada usuario hace a PRICO más útil." },
          ].map((v, i) => (
            <motion.div key={v.t} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.06 }}
              className="card-soft p-7">
              <div className="size-12 rounded-2xl grid place-items-center text-primary-foreground"
                style={{ background: "var(--gradient-primary)" }}>
                <v.i className="size-6" />
              </div>
              <h3 className="font-bold text-lg mt-4">{v.t}</h3>
              <p className="text-sm text-muted-foreground mt-1">{v.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="container-prico pb-24">
        <motion.div {...fadeUp} className="card-soft p-10 md:p-14 text-center" style={{ background: "var(--gradient-warm)" }}>
          <h3 className="text-3xl md:text-4xl font-black max-w-2xl mx-auto">¿Quieres formar parte del equipo o ser aliado?</h3>
          <p className="text-muted-foreground mt-3 max-w-xl mx-auto">Estamos creciendo. Si compartes nuestra misión, hablemos.</p>
          <Link to="/contacto" className="btn-primary mt-6">Hablemos <ArrowRight className="size-4" /></Link>
        </motion.div>
      </section>
    </>
  );
}
